"use client";

import { ArrowUpRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface Masterpiece {
  number: string;
  title: string;
  narrative: string;
  year: string;
  jurisdiction: string;
  outcome: string;
  image: string;
  srcset: string;
  sizes: string;
  alt: string;
}
const masterpieces: Masterpiece[] = [{
  number: "01",
  title: "Practical Life",
  narrative: "Children develop independence and coordination through purposeful activities. By mastering everyday tasks, they build the concentration and confidence necessary for all future academic pursuits.",
  year: "2.5-6",
  jurisdiction: "Focus",
  outcome: "Autonomy",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722029/img/practical_life_activity.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722029/img/practical_life_activity.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722029/img/practical_life_activity.png-wvc-sizes",
  alt: "Child carefully pouring water in a Montessori classroom"
}, {
  number: "02",
  title: "Sensorial Exploration",
  narrative: "Our environment invites children to refine their senses. Through tactile materials, they categorize the world around them, laying the foundation for mathematical and scientific thinking.",
  year: "2.5-6",
  jurisdiction: "Discovery",
  outcome: "Clarity",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722029/img/sensorial_materials.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722029/img/sensorial_materials.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722029/img/sensorial_materials.png-wvc-sizes",
  alt: "Montessori sensorial blocks arranged on a wooden shelf"
}, {
  number: "03",
  title: "Language Development",
  narrative: "We foster a rich linguistic environment where children explore phonetics and expression. Our approach ensures that reading and writing emerge as natural, joyful extensions of communication.",
  year: "2.5-6",
  jurisdiction: "Expression",
  outcome: "Fluency",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722029/img/language_learning.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722029/img/language_learning.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722029/img/language_learning.png-wvc-sizes",
  alt: "Child tracing sandpaper letters in a quiet classroom corner"
}, {
  number: "04",
  title: "Mathematical Mind",
  narrative: "Mathematics is made tangible through concrete materials. Children move from physical quantities to abstract concepts, developing a deep, intuitive understanding of numerical relationships.",
  year: "2.5-6",
  jurisdiction: "Logic",
  outcome: "Precision",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722029/img/math_materials.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722029/img/math_materials.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722029/img/math_materials.png-wvc-sizes",
  alt: "Golden beads used for decimal system lessons"
}];
function Reveal({
  children,
  delay = 0,
  className = ""
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div ref={ref} style={{
    transitionDelay: `${delay}ms`
  }} className={`transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"} ${className}`}>
      {children}
    </div>;
}
function MasterpieceBlock({
  piece,
  index
}: {
  piece: Masterpiece;
  index: number;
}) {
  const isReversed = index % 2 === 1;
  return <div data-index={index} className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
      <div className={`lg:col-span-6 ${isReversed ? "lg:order-2" : "lg:order-1"}`}>
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl aspect-[4/5] lg:aspect-[5/6] bg-white shadow-lg border border-border">
            <img src={piece.image} data-wvc-srcset={piece.srcset} data-wvc-sizes={piece.sizes} alt={piece.alt} loading="lazy" decoding="async" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent pointer-events-none" />
          </div>
        </Reveal>
      </div>

      <div className={`lg:col-span-6 ${isReversed ? "lg:order-1 lg:pr-8" : "lg:order-2 lg:pl-8"}`}>
        <Reveal delay={100}>
          <div className="font-serif italic text-orange-700 text-7xl md:text-8xl lg:text-[6rem] leading-none mb-8 tracking-tight">
            {piece.number}
          </div>
        </Reveal>

        <Reveal delay={200}>
          <h3 className="font-default text-3xl md:text-4xl lg:text-5xl leading-[1.05] text-green-950 mb-8">
            {piece.title}
          </h3>
        </Reveal>

        <Reveal delay={300}>
          <p className="font-default text-base md:text-lg leading-relaxed text-green-900 mb-10 max-w-xl font-light">
            {piece.narrative}
          </p>
        </Reveal>

        <Reveal delay={400}>
          <div className="grid grid-cols-3 gap-6 md:gap-8 mb-10 max-w-lg">
            <div>
              <div className="font-mono text-[0.65rem] uppercase tracking-[0.2em] text-green-800 mb-2">
                Ages
              </div>
              <div className="font-default text-lg md:text-xl text-green-950">
                {piece.year}
              </div>
            </div>
            <div>
              <div className="font-mono text-[0.65rem] uppercase tracking-[0.2em] text-green-800 mb-2">
                Focus
              </div>
              <div className="font-default text-lg md:text-xl text-green-950">
                {piece.jurisdiction}
              </div>
            </div>
            <div>
              <div className="font-mono text-[0.65rem] uppercase tracking-[0.2em] text-green-800 mb-2">
                Outcome
              </div>
              <div className="font-default text-lg md:text-xl text-green-950">
                {piece.outcome}
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={500}>
          <Button asChild className="rounded-lg px-8 py-6 h-auto font-default text-base bg-green-950 text-white hover:bg-green-900">
            <Link to="/OurProgramPage">
              Explore Program
              <ArrowUpRight className="ml-1 size-4" />
            </Link>
          </Button>
        </Reveal>
      </div>
    </div>;
}
export default function Methodology() {
  return <section data-section-id="3575" id="the-montessori-method" className="bg-background text-foreground py-24 md:py-32 px-6 md:px-10 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <Reveal>
          <div className="text-center mb-12 md:mb-16">
            <p className="font-mono text-xs md:text-sm uppercase tracking-[0.35em] text-green-800 mb-10">
              Our Educational Approach
            </p>
          </div>
        </Reveal>

        <Reveal delay={150}>
          <h2 className="font-default text-center text-5xl md:text-7xl lg:text-8xl leading-[0.95] text-green-950 mb-24 md:mb-32">
            Cultivating
            <br />
            <span className="italic text-orange-700 font-serif">Potential.</span>
          </h2>
        </Reveal>

        <div className="space-y-32 md:space-y-48">
          {masterpieces.map((piece, i) => <div key={i} data-index={i}>
              <MasterpieceBlock piece={piece} index={i} />

              {i < masterpieces.length - 1 && <Reveal>
                  <div className="flex items-center justify-center gap-6 mt-32 md:mt-48">
                    <div className="h-px flex-1 max-w-[200px] bg-green-200" />
                    <span className="font-default text-2xl text-orange-600 italic">
                      &lowast;
                    </span>
                    <div className="h-px flex-1 max-w-[200px] bg-green-200" />
                  </div>
                </Reveal>}
            </div>)}
        </div>

        <Reveal>
          <div className="flex items-center justify-center gap-6 mt-32 md:mt-40 mb-16">
            <div className="h-px flex-1 max-w-[200px] bg-green-200" />
            <span className="font-default text-2xl text-orange-600 italic">
              &lowast;
            </span>
            <div className="h-px flex-1 max-w-[200px] bg-green-200" />
          </div>
        </Reveal>

        <Reveal delay={200}>
          <div className="text-center">
            <Link to="/PhilosophyPage" className="inline-flex items-center gap-3 font-default italic text-xl md:text-2xl text-green-900 hover:text-green-950 transition-colors duration-500 group">
              <span className="border-b border-green-300 group-hover:border-green-950 pb-1">
                Read Our Philosophy
              </span>
              <ArrowUpRight className="size-5 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>;
}